import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import CustomNavbarClients from "../navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import Footer from "../Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faStore } from "@fortawesome/free-solid-svg-icons";
import "./Contact.scss";
import { IContactRequest } from "interfaces/IContact";
import { sendContactMessage } from "api/auth";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear any previous status when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: "" });
    }
  };

  const validateForm = (): boolean => {
    const { firstName, lastName, email, subject, message } = formData;

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !subject.trim() ||
      !message.trim()
    ) {
      setSubmitStatus({
        type: "error",
        message: "Todos los campos son obligatorios.",
      });
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitStatus({
        type: "error",
        message: "Por favor, ingresa un correo electrónico válido.",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API
      const contactData: IContactRequest = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      };

      const response = await sendContactMessage(contactData);

      if (response.success) {
        setSubmitStatus({
          type: "success",
          message:
            response.message ||
            "¡Mensaje enviado exitosamente! Te responderemos pronto.",
        });

        // Reset form after successful submission
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus({
          type: "error",
          message:
            response.message ||
            "Hubo un error al enviar el mensaje. Por favor, intenta nuevamente.",
        });
      }
    } catch (error: any) {
      // Handle different types of errors
      let errorMessage =
        "Error de conexión. Por favor, verifica tu internet e intenta nuevamente.";

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 422 && data.errors) {
          // Validation errors from backend
          const firstError = Object.values(data.errors)[0] as string[];
          errorMessage =
            firstError[0] || "Error de validación en el formulario.";
        } else if (data.message) {
          errorMessage = data.message;
        } else if (status >= 500) {
          errorMessage =
            "Error interno del servidor. Por favor, intenta más tarde.";
        }
      }

      setSubmitStatus({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomNavbarClients />
      <ClientMenuBar />

      <div className="contact-container">
        <Container>
          {/* Main Title */}
          <Row className="justify-content-center mb-5">
            <Col xs={12} className="text-center">
              <h1 className="contact-main-title">
                ¡Hola! ¿En qué podemos ayudarte?
              </h1>
            </Col>
          </Row>

          {/* Content Row */}
          <Row>
            {/* Left Column - Email Form */}
            <Col xs={12} md={6}>
              <div className="email-section">
                <div className="section-title-bar">
                  <h2 className="section-title">Envíanos un correo</h2>
                </div>

                <div className="email-content">
                  <div className="email-info">
                    <p className="email-address">boutique_marte@hotmail.com</p>
                    <p className="email-description">
                      Te responderemos de uno a dos días. Por favor, evita
                      enviar otro correo si ya enviaste uno anteriormente,
                      estamos trabajando en ello.
                    </p>
                  </div>

                  {/* Status Alert */}
                  {submitStatus.type && (
                    <Alert
                      variant={
                        submitStatus.type === "success" ? "success" : "danger"
                      }
                      className="mb-3"
                      dismissible
                      onClose={() =>
                        setSubmitStatus({ type: null, message: "" })
                      }
                    >
                      {submitStatus.message}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} className="contact-form">
                    <Row>
                      <Col xs={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            name="firstName"
                            placeholder="Nombre"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            name="lastName"
                            placeholder="Apellido"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Correo electrónico"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        name="subject"
                        placeholder="Asunto"
                        value={formData.subject}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="message"
                        placeholder="Mensaje"
                        value={formData.message}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        required
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      className="submit-btn w-100"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Enviando...
                        </>
                      ) : (
                        "Enviar"
                      )}
                    </Button>
                  </Form>
                </div>
              </div>
            </Col>

            {/* Right Column - Contact Info */}
            <Col xs={12} md={6}>
              <div className="right-column-container">
                {/* Call Section */}
                <div className="info-card">
                  <div className="info-title-bar">
                    <h3 className="info-title">Llámanos</h3>
                  </div>
                  <div className="info-content">
                    <FontAwesomeIcon icon={faPhone} className="info-icon" />
                    <div className="info-details">
                      <p className="phone-number">868 265 3942</p>
                      <p className="schedule">
                        de Lunes a Sábado: 10 a.m. - 7 p.m.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Visit Section */}
                <div className="info-card">
                  <div className="info-title-bar">
                    <h3 className="info-title">Visítanos</h3>
                  </div>
                  <div className="info-content">
                    <FontAwesomeIcon icon={faStore} className="info-icon" />
                    <div className="info-details">
                      <p className="address">
                        <strong>Av Marte R. Gómez 403</strong>
                        <br />
                        Francisco I. Madero
                        <br />
                        87395 Heroica Matamoros, Tamps.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Footer />
    </>
  );
};

export default Contact;
